import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ShieldCheck, ShieldQuestion, ShieldAlert, FileCheck, Users, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Hero */}
      <section className="bg-muted/30 border-b border-border">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-foreground mb-4">
              Trust & Verification
            </h1>
            <p className="text-lg text-muted-foreground">
              Understanding how we verify profiles and what our trust badges mean for parents, 
              schools, and organisations looking for camping experiences.
            </p>
          </div>
        </div>
      </section>

      {/* Verification Status Explained */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading font-semibold text-2xl md:text-3xl text-foreground mb-8">
            Verification Status Explained
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="w-12 h-12 bg-primary/15 rounded-xl flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                Verified
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                This profile has been reviewed and verified by our team. The facilitator or camp 
                has provided documentation and undergone our verification process. This is the 
                highest level of trust on our platform.
              </p>
            </div>

            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mb-4">
                <ShieldQuestion className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                Unverified
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                This profile has been created but has not yet completed our verification process. 
                This doesn't mean the profile is fraudulent, only that we haven't verified their 
                credentials yet.
              </p>
            </div>

            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="w-12 h-12 bg-destructive/15 rounded-xl flex items-center justify-center mb-4">
                <ShieldAlert className="w-6 h-6 text-destructive" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                Flagged
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                This profile has been flagged for review. This may be due to concerns raised by 
                users, incomplete verification, or issues identified during our review process. 
                Exercise caution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Verify */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading font-semibold text-2xl md:text-3xl text-foreground mb-8">
            What We Verify
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-primary" />
                <h3 className="font-heading font-semibold text-lg">For Facilitators</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Identity verification (ID document)</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Relevant qualifications and certifications</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Police clearance status</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Professional references from the camp sector</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>First aid certification level</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Consent for background checks</span>
                </li>
              </ul>
            </div>

            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-secondary" />
                <h3 className="font-heading font-semibold text-lg">For Camps & Venues</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Business registration or ownership</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Physical location verification</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Industry accreditations (if claimed)</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-1 text-primary" />
                  <span>Safety and facility standards</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 p-6 bg-card rounded-2xl border border-border" data-testid="verified-summary-note">
            <p className="text-foreground text-base">
              Verified facilitators have had their identity, police clearance, qualifications and
              professional references reviewed by the Campia team.
            </p>
          </div>

          <div className="mt-8 p-6 bg-primary/5 rounded-2xl border border-primary/20">
            <h4 className="font-heading font-semibold mb-2">Important Note</h4>
            <p className="text-muted-foreground text-sm">
              While we strive to verify all information provided, we cannot guarantee the accuracy 
              of all claims. Parents and organisations should always conduct their own due diligence 
              when selecting camps or facilitators for their children or students.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-primary rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-heading font-semibold text-2xl md:text-3xl text-primary-foreground mb-4">
              Ready to Get Listed?
            </h2>
            <p className="text-primary-foreground/80 max-w-xl mx-auto mb-6">
              Join South Africa's trusted camp directory and start connecting with parents and schools 
              looking for quality camping experiences.
            </p>
            <Button 
              size="lg" 
              variant="secondary"
              className="rounded-full px-8"
              asChild
            >
              <Link to="/register">
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
